import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useCallback } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { MotiView } from "moti";
import { ClipboardText, Users, CopySimple, Check, ArrowLeft } from "@phosphor-icons/react";

import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CourseTabNavigator from "../../components/adminDashboard/CourseTabNavigator/CourseTabNavigator";
import AdminDegreeRosterManagementContainer from "../../containers/adminDashboard/AdminDegreeRosterManagementContainer/AdminDegreeRosterManagementContainer";
import { NavigationProp, useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { useGetDegreesByDegreeIdQuery } from "../../graphql/generated/graphql";
import Tooltip from "../../components/common/tooltip/Tooltip";
import { useAppConfig } from "../../AppConfigProvider";
import { usePreviewMode } from "../../contexts/PreviewModeContext";
import AdminDegreeManageCoursesContainer from "../../containers/adminDashboard/adminDegreeManageCoursesContainer/AdminDegreeManageCoursesContainer";

const StudentsIcon = () => <Users color="#FFFFFF" size={18} />;
const AssignmentsIcon = () => <ClipboardText color="#FFFFFF" size={18} />;
const CopyIcon = ({ isCopied }: { isCopied: boolean }) =>
  isCopied ? <Check color="#10B981" size={24} /> : <CopySimple color="#3E68FE" size={24} />;

export enum DegreeInformationTab {
  ROSTER_MANAGEMENT = "roster-management",
  MANAGE_COURSES = "manage-courses",
}

const TAB_DATA = [
  { key: DegreeInformationTab.ROSTER_MANAGEMENT, title: "Roster Management" },
  { key: DegreeInformationTab.MANAGE_COURSES, title: "Manage Courses" },
];

function DegreeInformationView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Degree Information">>();
  const { enterPreviewMode } = usePreviewMode();
  const appConfig = useAppConfig();

  const degreeId = route.params?.degreeId;
  const [activeTab, setActiveTab] = useState(DegreeInformationTab.ROSTER_MANAGEMENT);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (route.params?.activeTab) {
        const tabParam = route.params.activeTab as DegreeInformationTab;
        if (Object.values(DegreeInformationTab).includes(tabParam)) {
          setActiveTab(tabParam);
        }
      }
    }, [route.params?.activeTab])
  );

  const {
    data: degreeData,
    refetch: refetchDegreeData,
    loading,
    error,
  } = useGetDegreesByDegreeIdQuery({
    variables: { id: degreeId },
    skip: !degreeId,
  });

  const degree = degreeData?.getDegree?.data?.[0];

  if (loading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (error || !degree) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="Degree not found" size="l" useTranslationText={false} />
          </View>
        </View>
      </AdminLayout>
    );
  }

  // Calculate stats
  const enrolledStudents = degree.usersEnrolledIn?.length || 0;
  const courseCount = degree.coursesHasCourse?.length || 0;
  // Placeholder for average grade - we need to implement this calculation
  // hide for now
  // const averageGrade = 87; // Placeholder

  const handleCopyDegreeLink = () => {
    const courseLink = `https://${appConfig.currentDomain}/admin/degree-information?degree_id=${degree.id}`;
    navigator.clipboard.writeText(courseLink);
    setIsLinkCopied(true);
    setTimeout(() => setIsLinkCopied(false), 1000);
  };

  const handleBackToDegreePrograms = () => {
    const returnTo = route.params?.returnTo || "Degrees & Programs";
    navigation.navigate(returnTo as never);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case DegreeInformationTab.ROSTER_MANAGEMENT:
        return <AdminDegreeRosterManagementContainer degreeId={degreeId} refetchDegreeData={refetchDegreeData} />;
      case DegreeInformationTab.MANAGE_COURSES:
        return <AdminDegreeManageCoursesContainer degree={degree} />;
      default:
        return null;
    }
  };

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            {/* Back Button */}
            <CustomButton
              text="Back"
              styleType="transparent"
              onPress={handleBackToDegreePrograms}
              useTranslationText={false}
              style={styles.backButton}
              textStyle={styles.backButtonText}
              leftIcon={<ArrowLeft size={16} color="#64748B" />}
            />

            <CustomSpacing type="vertical" size="l" />

            {/* Header and Stats Container */}
            <View style={styles.headerStatsContainer}>
              {/* Header Section */}
              <View style={styles.headerContainer}>
                <View style={styles.headerContent}>
                  <View style={styles.titleSection}>
                    <CustomText
                      text={degree.name}
                      useTranslationText={false}
                      textType="display"
                      weight="ultraBold"
                      role="heading"
                      aria-level="1"
                      style={styles.courseTitle}
                    />
                  </View>

                  <CustomText
                    text={degree.description} //need to fix date formatting
                    size="m"
                    weight="medium"
                    useTranslationText={false}
                    style={styles.courseSubtitle}
                  />
                </View>
              </View>

              <CustomSpacing type="vertical" size="xl" />

              {/* Stats Cards */}
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <View style={[styles.statIcon, styles.studentsIcon]}>
                    <StudentsIcon />
                  </View>
                  <View style={styles.statContent}>
                    <CustomText
                      text={enrolledStudents.toString()}
                      size="xxl"
                      weight="ultraBold"
                      useTranslationText={false}
                      style={styles.statNumber}
                    />
                    <CustomText
                      text="ENROLLED STUDENTS"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.statLabel}
                    />
                  </View>
                </View>

                <View style={styles.statCard}>
                  <View style={[styles.statIcon, styles.assignmentsIcon]}>
                    <AssignmentsIcon />
                  </View>
                  <View style={styles.statContent}>
                    <CustomText
                      text={courseCount.toString()}
                      size="xxl"
                      weight="ultraBold"
                      useTranslationText={false}
                      style={styles.statNumber}
                    />
                    <CustomText
                      text="COURSES"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.statLabel}
                    />
                  </View>
                </View>
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            {/* Tab Navigator */}
            <View style={styles.tabContainer}>
              <CourseTabNavigator
                tabData={TAB_DATA}
                activeTab={activeTab}
                onTabPress={(tabKey: string) => setActiveTab(tabKey as DegreeInformationTab)}
              />

              {renderTabContent()}
            </View>
          </MotiView>
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    // @ts-expect-error - web only
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  backButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 0,
  },
  backButtonText: {
    color: "#64748B",
    fontSize: 16,
  },
  headerStatsContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    marginHorizontal: 0,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerContent: {
    flex: 1,
  },
  titleSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  courseTitle: {
    color: "#1e293b",
    fontSize: 28,
  },
  courseSubtitle: {
    color: "#64748b",
    marginTop: 4,
  },
  statsContainer: {
    flexDirection: "row",
    gap: 24,
  },
  statCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 18,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  studentsIcon: {
    backgroundColor: "#913DAF",
  },
  assignmentsIcon: {
    backgroundColor: "#1A8E9D",
  },
  // gradeIcon: {
  //   backgroundColor: "#22C55E",
  // },
  statContent: {
    flex: 1,
  },
  statNumber: {
    color: "#101828",
    lineHeight: 32,
  },
  statLabel: {
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 4,
  },
  tabContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
});

export default DegreeInformationView;
